import { wrapCurrency } from '@/utils/common/number';
import useTranslation from 'next-translate/useTranslation';
import { twMerge } from 'tailwind-merge';
import { ProductCardProps } from '../../types';
import { useMemo } from 'react';

type ProductPriceV2Props = Pick<ProductCardProps, 'data' | 'priceSize'> & {
  showBasePrice?: boolean;
  showVolumePrice?: boolean;
  showSpecialPrice?: boolean;
  priceContainer?: string;
};

const ProductPriceV2: React.FC<Partial<ProductPriceV2Props>> = props => {
  const { t } = useTranslation('common');
  const {
    data,
    showBasePrice = true,
    showVolumePrice = true,
    showSpecialPrice,
    priceContainer,
    priceSize = 'base'
  } = props;

  const priceStyle = useMemo(() => {
    if (priceSize === 'large') {
      return {
        currency: 'text-[20px] leading-100',
        integer: 'text-[48px] leading-100',
        decimal: 'text-[20px] leading-100',
        unitPrice: 'text-[16px] leading-100',
        basePrice: 'text-[16px] leading-100'
      };
    }
    return {
      currency: 'text-[14px] leading-100',
      integer: 'text-[35px] leading-100',
      decimal: 'text-[14px] leading-100',
      unitPrice: 'text-[12px] leading-100',
      basePrice: 'text-[12px] leading-100'
    };
  }, [priceSize]);

  // 使用与 product-price.tsx 相同的价格逻辑
  const finalPrice = (showSpecialPrice ? data?.special_price : data?.price) ?? 0;

  // 价格展示划分成整数部分和小数部分
  const priceParts = finalPrice.toString().split('.');
  const integerPrice = priceParts[0] ?? '0';
  const _decimalPrice = priceParts[1];
  const decimalPrice = _decimalPrice ? `${_decimalPrice}0`.slice(0, 2) : '00';

  // 是否显示 base_price (与 product-price.tsx 逻辑一致)
  const shouldShowBasePrice = !!(showBasePrice && data?.base_price && data.base_price > (data?.special_price ?? 0));

  // 价格颜色
  const priceColor = 'text-pricing-txt';

  // 渲染右侧价格部分 (小数 + base_price/volume提示)
  const renderRightPrice = () => {
    // Volume Price 模式
    if (data?.volume_price_support && showVolumePrice) {
      return (
        <div className="flex items-start">
          <span
            className={twMerge(priceColor, priceStyle.decimal, 'font-semibold')}
            data-testid="wid-product-card-price-decimal">
            {decimalPrice}
          </span>
          <span
            className={twMerge(priceColor, priceStyle.unitPrice, 'ml-0.5')}
            data-testid="wid-product-card-price-volume-text">
            {t('volume_add_abbreviation', { count: data?.volume_threshold })}
          </span>
        </div>
      );
    }

    // 普通价格模式
    return (
      <div className="flex items-start">
        <span
          className={twMerge(priceColor, priceStyle.decimal, 'font-semibold')}
          data-testid="wid-product-card-price-decimal">
          {decimalPrice}
        </span>
        {shouldShowBasePrice && data.base_price && (
          <span className={twMerge(priceStyle.basePrice, 'text-surface-100-fg-minor line-through ml-0.5')}>
            ${wrapCurrency(data.base_price)}
          </span>
        )}
      </div>
    );
  };

  // 渲染底部价格信息
  const renderBottomPrice = () => {
    // Volume Price 模式: 显示 base_price、volume_for_one 和 unit_price
    if (data?.volume_price_support && showVolumePrice && Number(data?.volume_price) > 0) {
      return (
        <div className="flex items-start">
          <div className={twMerge(priceStyle.unitPrice, 'text-surface-100-fg-minor flex items-center')}>
            {shouldShowBasePrice && data.base_price && (
              <span data-testid="wid-product-card-base-price" className="line-through">
                ${wrapCurrency(data.base_price)}
              </span>
            )}
            {data?.volume_price && (
              <>
                {shouldShowBasePrice && data.base_price && (
                  <span className="inline-block w-1 h-1 mx-1 bg-shade-cool-light-7 rounded-full"></span>
                )}
                <span data-testid="wid-product-card-volume-pricing-for-one">
                  {t('volume_for_one_attr', { count: wrapCurrency(data.volume_price) })}
                </span>
              </>
            )}
            {data?.unit_price && (
              <>
                {(shouldShowBasePrice && data.base_price) || data?.volume_price ? (
                  <span className="inline-block w-1 h-1 mx-1 bg-shade-cool-light-7 rounded-full"></span>
                ) : null}
                <span data-testid="wid-product-card-unit-price">{data.unit_price}</span>
              </>
            )}
          </div>
        </div>
      );
    }

    // 非 Volume Price 模式: 只显示 unit_price (如果有)
    if (data?.unit_price) {
      return (
        <div className="flex items-start">
          <div className={twMerge(priceStyle.unitPrice, 'text-surface-100-fg-minor')}>
            <span data-testid="wid-product-card-unit-price">{data.unit_price}</span>
          </div>
        </div>
      );
    }

    return null;
  };

  // 主价格布局: 保持原有的堆叠结构
  return (
    <div className={twMerge('flex items-start', priceContainer)}>
      <div className={twMerge(priceColor, priceStyle.currency, 'font-semibold')}>$</div>
      <div
        className={twMerge(priceColor, priceStyle.integer, 'mx-0.5 font-bold -mt-0.5')}
        data-testid="wid-product-card-price-integer">
        {integerPrice}
      </div>
      <div>
        {renderRightPrice()}
        {renderBottomPrice()}
      </div>
    </div>
  );
};

export default ProductPriceV2;
